<template>
  <div>
    <v-row class="py-0">
      <v-col cols="6">
        <h3 class="title dw_green--text font-weight-light">
          <v-icon color="dw_green" class="mr-2 mb-1" v-text="mdiCrownOutline" />
          Update an Existing Play
        </h3>
        <v-form ref="updatePlayForm" v-model="formValid" @submit.prevent>
        <v-card flat color="bg_dark">
          <v-card-text class="py-0">
            <v-autocomplete
              v-model="playChoice"
              :items="playOptions"
              :search-input.sync="search"
              color="dw_green"
              item-color="dw_green"
              item-text="display_name"
              item-value="id"
              label="Selected Play"
              placeholder="Start typing to search"
              return-object
              clearable
              :prepend-icon="mdiDatabaseSearch"
              @change="(item) => item !== undefined ? fetchPlayByID(item.id) : cancelChanges()"
            >
            </v-autocomplete>
          </v-card-text>
          <v-divider></v-divider>
          <v-expand-transition>
            <v-list v-if="playChoice && selectedPlay && selectedForEdit" class="bg_dark">
              <form-text-field
                ref="editNameField"
                :existing="selectedForEdit.name"
                label="Name *"
                @fieldChanged="(e) => playEdited('name', e)" />
               <form-text-area
                ref="editDescriptionField"
                :existing="selectedForEdit.description"
                label="Description *"
                @fieldChanged="(e) => playEdited('description', e)" />
              <form-select-chips
                ref="editCAPECsField"
                :existingIDs="selectedForEdit.attackpatterns"
                :options="capecOptions"
                label="CAPEC&trade; Attack Patterns"
                prefix="CAPEC-"
                shorttext="id"
                longtext="display_name"
                @fieldChanged="(e) => playEdited('attackpatterns', e)" />
              <form-select-chips
                ref="editTechniquesField"
                :existingIDs="selectedForEdit.techniques"
                :options="techniqueOptions"
                label="MITRE ATT&amp;CK&reg; Techniques"
                shorttext="mitre_id"
                longtext="display_name"
                @fieldChanged="(e) => playEdited('techniques', e)" />
              <form-select-chips
                ref="editControlsField"
                :existingIDs="selectedForEdit.controls"
                :options="controlOptions"
                label="NIST SP 800-53 (Rev. 5) Controls"
                shorttext="label"
                longtext="display_name"
                @fieldChanged="(e) => playEdited('controls', e)" />
              <form-select-chips
                ref="editCWEsField"
                :existingIDs="selectedForEdit.cwes"
                :options="cweOptions"
                label="CWE&trade; Weaknesses"
                prefix="CWE-"
                shorttext="id"
                longtext="display_name"
                @fieldChanged="(e) => playEdited('cwes', e)" />
              <form-select-chips
                ref="editToolsField"
                :existingIDs="selectedForEdit.tools"
                :options="toolOptions"
                label="Tools"
                shorttext="name"
                longtext="name"
                @fieldChanged="val => { playEdited('tools', val); }" />
              <form-select-chips
                ref="editTagsField"
                :existingIDs="selectedForEdit.tags"
                :options="tagOptions"
                label="Tags"
                shorttext="name"
                longtext="name"
                @fieldChanged="(e) => playEdited('tags', e)" />
              <form-select-chips
                ref="editObjectivesField"
                :existingIDs="selectedForEdit.objectives"
                :options="objectiveOptions"
                label="Objectives"
                shorttext="title"
                longtext="title"
                @fieldChanged="(e) => playEdited('objectives', e)" />
            </v-list>
          </v-expand-transition>
          <v-card-actions v-if="playChoice && selectedPlay">
            <v-spacer></v-spacer>
            <v-btn
              type="button"
              @click="changesMade ? $refs.confirmCancel.dialog = true : resetForm()"
              color="dw_green"
              x-small
              text>Cancel</v-btn>
            <v-btn
              type="submit"
              :disabled="!formValid || !changesMade || formsOpen()"
              @click="savePlayEdits"
              outlined
              small
              color="dw_green">
              Save
              <v-icon x-small color="dw_green" class="ml-1" v-text="mdiCrown" />
            </v-btn>
          </v-card-actions>
        </v-card>
        </v-form>
      </v-col>
      <v-col cols="6" v-if="playChoice && selectedPlay && selectedForEdit">
        <v-card flat color="bg_dark">
          <v-card-title class="font-weight-light text--grey">
            <v-icon class="mr-3" v-text="mdiPencilOutline" />
            Original Play - {{ selectedPlay.id }}
          </v-card-title>
          <v-card-text class="py-0">
            <v-list>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    <v-icon small color="grey" class="mr-1 mb-1" v-text="mdiCrownOutline" />
                    Name
                  </v-list-item-subtitle>
                  <span :class="edited.name ? 'red--text text-decoration-line-through'
                    : 'grey--text'">{{ selectedPlay.name }}</span>
                  <span v-if="edited.name" class="green--text">
                    {{ selectedForEdit.name }}
                  </span>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    <v-icon small color="grey" class="mr-1 mb-1" v-text="mdiCrownOutline" />
                    Description
                  </v-list-item-subtitle>
                  <span :class="edited.description ? 'red--text text-decoration-line-through'
                    : 'grey--text'">{{ selectedPlay.description }}</span>
                  <span v-if="edited.description" class="green--text">
                    {{ selectedForEdit.description }}
                  </span>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    <v-icon small color="grey" class="mr-1 mb-1" v-text="mdiAbacus" />
                    CAPEC&trade; Attack Patterns
                  </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="compare.attackpatterns.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated Attack Patterns</v-chip>
                    <v-chip
                      v-else
                      v-for="capec in compare.attackpatterns"
                      :key="`capec.${capec.val}`"
                      :ripple="false"
                      label
                      outlined
                      small
                      :color="capec.color"
                      :class="capec.color === 'red' ? 'text-decoration-line-through': ''">
                      {{ capec.val }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    <v-icon small color="grey" class="mr-1 mb-1" v-text="mdiStrategy" />
                    MITRE ATT&amp;CK&reg; Techniques
                  </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="compare.techniques.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated Techniques</v-chip>
                    <v-chip
                      v-else
                      v-for="teq in compare.techniques"
                      :key="`teq.${teq.val}`"
                      :ripple="false"
                      label
                      outlined
                      small
                      :color="teq.color"
                      :class="teq.color === 'red' ? 'text-decoration-line-through': ''">
                      {{ teq.val }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    <v-icon small color="grey" class="mr-1 mb-1" v-text="mdiSwordCross" />
                    NIST SP 800-53 (Rev. 5) Controls
                  </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="compare.controls.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated NIST SP 800-53 Controls</v-chip>
                    <v-chip
                      v-else
                      v-for="ctrl in compare.controls"
                      :key="`ctrl.${ctrl.val}`"
                      :ripple="false"
                      label
                      outlined
                      small
                      :color="ctrl.color"
                      :class="ctrl.color === 'red' ? 'text-decoration-line-through': ''">
                      {{ ctrl.val }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    <v-icon small color="grey" class="mr-1 mb-1" v-text="mdiShieldHalfFull" />
                    CWE&trade; Weaknesses
                  </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="compare.cwes.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated Weaknesses</v-chip>
                    <v-chip
                      v-else
                      v-for="cwe in compare.cwes"
                      :key="`cwe.${cwe.val}`"
                      :ripple="false"
                      label
                      outlined
                      small
                      :color="cwe.color"
                      :class="cwe.color === 'red' ? 'text-decoration-line-through': ''">
                      {{ cwe.val }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    <v-icon small color="grey" class="mr-1 mb-1" v-text="mdiPaletteSwatchOutline" />
                    Tools
                    </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="compare.tools.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated Tools</v-chip>
                    <v-chip
                      v-for="tool in compare.tools"
                      :key="`tool.${tool.val}`"
                      :ripple="false"
                      label
                      outlined
                      small
                      :color="tool.color"
                      :class="tool.color === 'red' ? 'text-decoration-line-through': ''">
                      {{ tool.val }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    <v-icon small color="grey" class="mr-1 mb-1" v-text="mdiPound" />
                    Tags
                  </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="compare.tags.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated Tags</v-chip>
                    <v-chip
                      v-for="tag in compare.tags"
                      :key="`tag.${tag.val}`"
                      :ripple="false"
                      label
                      outlined
                      small
                      :color="tag.color"
                      :class="tag.color === 'red' ? 'text-decoration-line-through': ''">
                      {{ tag.val }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle>
                    <v-icon small color="grey" class="mr-1 mb-1" v-text="mdiCompassOutline" />
                    Objectives
                  </v-list-item-subtitle>
                  <v-chip-group column>
                    <v-chip
                      v-if="compare.objectives.length === 0"
                      label
                      outlined
                      small
                      color="grey">No Associated Objectives</v-chip>
                    <v-chip
                      v-for="obj in compare.objectives"
                      :key="`obj.${obj.val}`"
                      :ripple="false"
                      label
                      outlined
                      small
                      :color="obj.color"
                      :class="obj.color === 'red' ? 'text-decoration-line-through': ''">
                      {{ obj.val }}
                    </v-chip>
                  </v-chip-group>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <confirm-cancel ref="confirmCancel" @confirmed="cancelChanges" />
  </div>
</template>

<script>
import {
  mdiCrownOutline,
  mdiDatabaseSearch,
  mdiPencilOutline,
  mdiCrown,
  mdiAbacus,
  mdiStrategy,
  mdiSwordCross,
  mdiShieldHalfFull,
  mdiPaletteSwatchOutline,
  mdiPound,
  mdiCompassOutline
} from '@mdi/js';
import { mapActions, mapGetters } from 'vuex';
import formTextField from '@/components/forms/fields/formTextField.vue';
import formTextArea from '@/components/forms/fields/formTextArea.vue';
import formSelectChips from '@/components/forms/fields/formSelectChips.vue';
import confirmCancel from '@/components/notifications/confirmCancel.vue';

export default {
  name: 'editPlay',
  components: {
    formTextField,
    formTextArea,
    formSelectChips,
    confirmCancel
  },
  data() {
    return {
      mdiCrownOutline,
      mdiDatabaseSearch,
      mdiPencilOutline,
      mdiCrown,
      mdiAbacus,
      mdiStrategy,
      mdiSwordCross,
      mdiShieldHalfFull,
      mdiPaletteSwatchOutline,
      mdiPound,
      mdiCompassOutline,
      formValid: false,
      required: (v) => !!v || 'Required',
      minPlay: (v) => (v && v.length >= 3) || 'Play must be at least 3 characters',
      nameRegex: (v) => v.match(/^([^<>]{3,256})$/gm) || 'Invalid characters',
      playChoice: undefined,
      search: null,
      selectedForEdit: undefined,
      changesMade: false,
      existingName: '',
      edited: {
        name: false,
        description: false,
        attackpatterns: false,
        techniques: false,
        controls: false,
        cwes: false,
        tools: false,
        tags: false,
        objectives: false
      },
      compare: {
        attackpatterns: [],
        techniques: [],
        controls: [],
        cwes: [],
        tools: [],
        tags: [],
        objectives: []
      }
    };
  },
  watch: {
    selectedPlay() {
      this.selectedForEdit = { ...this.selectedPlay };
      this.existingName = this.selectedPlay.name;
      this.compare = {
        attackpatterns: this.selectedPlay.attackpatterns.map((ctrl) => ({ val: `CAPEC-${ctrl.id}`, color: 'grey' })),
        techniques: this.selectedPlay.techniques.map((tq) => ({ val: tq.mitre_id, color: 'grey' })),
        controls: this.selectedPlay.controls.map((ctrl) => ({ val: ctrl.label, color: 'grey' })),
        cwes: this.selectedPlay.cwes.map((ctrl) => ({ val: `CWE-${ctrl.id}`, color: 'grey' })),
        tools: this.selectedPlay.tools.map((tool) => ({ val: tool.name, color: 'grey' })),
        tags: this.selectedPlay.tags.map((tag) => ({ val: tag.name, color: 'grey' })),
        objectives: this.selectedPlay.objectives.map((obj) => ({ val: obj.title, color: 'grey' }))
      };
    }
  },
  computed: {
    ...mapGetters([
      'playOptions',
      'selectedPlay',
      'capecOptions',
      'techniqueOptions',
      'controlOptions',
      'cweOptions',
      'toolOptions',
      'tagOptions',
      'objectiveOptions'
    ])
  },
  mounted() {
    this.fetchFilterOptions();
  },
  methods: {
    ...mapActions([
      'fetchPlayByID',
      'fetchFilterOptions',
      'updatePlay'
    ]),
    async selectPlay(item) {
      await this.fetchPlayByID(item.id);
    },
    formsOpen() {
      if (this.$refs.editNameField.formField
      || this.$refs.editDescriptionField.formField
      || this.$refs.editCAPECsField.formField
      || this.$refs.editTechniquesField.formField
      || this.$refs.editControlsField.formField
      || this.$refs.editCWEsField.formField
      || this.$refs.editToolsField.formField
      || this.$refs.editTagsField.formField
      || this.$refs.editObjectivesField.formField
      ) {
        return true;
      }
      return false;
    },
    playEdited(field, val) {
      this.selectedForEdit[field] = val;
      this.edited[field] = this.selectedPlay[field] !== this.selectedForEdit[field];
      if (field !== 'name' && field !== 'description') {
        let field2;
        switch (field) {
          case 'attackpatterns':
            field2 = 'custom_id';
            break;
          case 'techniques':
            field2 = 'mitre_id';
            break;
          case 'controls':
            field2 = 'label';
            break;
          case 'cwes':
            field2 = 'custom_id';
            break;
          case 'objectives':
            field2 = 'title';
            break;
          default: // 'tools' or 'tags'
            field2 = 'name';
            break;
        }
        const orig = this.selectedPlay[field].map((x) => {
          if (field === 'attackpatterns') return { id: x.id, custom_id: `CAPEC-${x.id}` };
          if (field === 'cwes') return { id: x.id, custom_id: `CWE-${x.id}` };
          return { id: x.id, [field2]: x[field2] };
        });
        const changed = val.map((y) => {
          if (field === 'attackpatterns') return { id: y.id, custom_id: `CAPEC-${y.id}` };
          if (field === 'cwes') return { id: y.id, custom_id: `CWE-${y.id}` };
          return { id: y.id, [field2]: y[field2] };
        });
        const all = [...orig, ...changed];
        let vals = all.map((z) => {
          const inOld = orig.find((x) => x.id === z.id);
          const inNew = changed.find((y) => y.id === z.id);
          let color = inOld === undefined ? 'green' : 'grey';
          color = inNew === undefined ? 'red' : color;
          return {
            val: z[field2],
            color
          };
        });
        vals = vals.reduce((acc, curr) => {
          const exists = acc.find((z) => (z.val === curr.val));
          if (exists === undefined) acc.push(curr);
          return acc;
        }, []);
        this.compare[field] = vals;
        let comparisons = this.compare[field].map((v) => v.color);
        comparisons = [...new Set(comparisons)];
        if (comparisons.length === 1 && comparisons[0] === 'grey') this.edited[field] = false;
      }
      const edits = Object.values(this.edited);
      this.changesMade = edits.some((e) => e);
    },
    async savePlayEdits() {
      const editedPlay = {
        id: this.selectedForEdit.id,
        name: this.selectedForEdit.name,
        description: this.selectedForEdit.description,
        attackpatterns: this.selectedForEdit.attackpatterns.map((capec) => capec.id),
        techniques: this.selectedForEdit.techniques.map((teq) => teq.id),
        controls: this.selectedForEdit.controls.map((ctrl) => ctrl.id),
        cwes: this.selectedForEdit.cwes.map((cwe) => cwe.id),
        tools: this.selectedForEdit.tools.map((tool) => tool.id),
        tags: this.selectedForEdit.tags.map((tag) => tag.id),
        objectives: this.selectedForEdit.objectives.map((obj) => obj.id)
      };
      try {
        await this.updatePlay(editedPlay);
        this.resetForm();
        await this.fetchPlayByID(this.selectedPlay.id);
      } catch (err) {
        console.error(err);
      }
    },
    cancelChanges() {
      this.playChoice = undefined;
      this.selectedForEdit = undefined;
      this.resetForm();
    },
    resetForm() {
      this.changesMade = false;
      this.edited = {
        name: false,
        description: false,
        attackpatterns: false,
        techniques: false,
        controls: false,
        cwes: false,
        tools: false,
        tags: false,
        objectives: false
      };
      this.compare = {
        attackpatterns: [],
        techniques: [],
        controls: [],
        cwes: [],
        tools: [],
        tags: [],
        objectives: []
      };
    }
  }
};
</script>
